/**
 * /billing - Customer billing dashboard (OCP-117). Tabs:
 *   - Invoices  - list + status filter, PDF resolved per click
 *   - Orders    - the contract record (OCP-526)
 *   - Wallet    - prepaid balance + top-ups
 *   - Payment-methods - saved methods + the hosted update flow
 *   - Profile   - VAT-ID + invoice-address, mirrored to the invoicing provider
 *
 * Tab state is held in the URL `?tab=` so a customer can deep-link
 * to a specific tab from a Slack message or a payment-failed email.
 *
 * OCP-532 - nothing on this page is Paddle-shaped any more. It used to render
 * `paddle_invoice_id`, ask for a Paddle-hosted update link, and show an empty
 * table to every customer billed through the live Merchant of Record. Three
 * consequences of that are now explicitly handled in the UI:
 *
 *  1. Invoice rows carry a provider + a provider-neutral number, and the PDF is
 *     resolved on click (Polar signs its invoice links for minutes, so there is
 *     no durable URL to render as an <a href>).
 *  2. The payment-method list reports WHY a provider contributed nothing, so an
 *     empty list is never silently mistaken for "no card saved".
 *  3. Saving the VAT-ID reports whether the change actually reached the
 *     provider that issues the invoice (AGB V4 6.3), so the customer gets to
 *     see it land.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmtNumber, fmtOrbotoTokens } from '../lib/format-money';
import { useNavigate, useSearchParams } from 'react-router-dom';

import i18n, { intlTag } from '../i18n';

import { ApiError, api } from '../lib/api';
import { contractGateMessage } from '../components/BusinessCapacityGate';
import { formatMoney, useOrderReview } from '../components/OrderReviewStep';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDate } from '../lib/format-time';

type BillingProvider = 'paddle' | 'polar';

interface Invoice {
  id: string;
  provider: BillingProvider;
  provider_invoice_id: string;
  provider_invoice_number: string | null;
  subscription_id: string | null;
  total_eur_cents: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'refunded';
  issued_at: string;
  due_at: string | null;
  paid_at: string | null;
  pdf_url: string | null;
  pdf_available: boolean;
}

interface PaymentMethod {
  id: string;
  provider: BillingProvider;
  provider_method_id: string;
  type: string;
  last4: string | null;
  card_brand: string | null;
  expires_month: number | null;
  expires_year: number | null;
  status: 'pending' | 'verified' | 'expired' | 'failed';
  is_default: boolean;
  verified_at: string | null;
}

interface PaymentMethodSource {
  provider: BillingProvider;
  outcome: 'ok' | 'not_configured' | 'no_customer_link' | 'error';
  count: number;
  detail: string | null;
}

interface TaxMirrorReport {
  status: 'not_needed' | 'no_billing_provider' | 'mirrored' | 'partial' | 'failed';
  targets: Array<{
    provider: BillingProvider;
    outcome: 'mirrored' | 'not_configured' | 'no_customer_link' | 'error';
    detail: string | null;
  }>;
}

interface Profile {
  email: string;
  display_name: string | null;
  company_name: string | null;
  vat_id: string | null;
  invoice_address: InvoiceAddress | null;
  default_currency: string;
  paddle_customer_id: string | null;
  polar_customer_id: string | null;
  billing_providers: BillingProvider[];
  tax_mirror: TaxMirrorReport | null;
}

interface InvoiceAddress {
  street: string;
  street2?: string | null;
  city: string;
  postal_code: string;
  region?: string | null;
  country: string;
}

type Tab = 'invoices' | 'orders' | 'wallet' | 'payment-methods' | 'profile';
/** Tab order; labels are `tab.<id>` in the `billing` catalogue (OCP-656). OCP-526 added `orders`, the contract record. */
const TABS: readonly Tab[] = ['invoices', 'orders', 'wallet', 'payment-methods', 'profile'];

interface WalletLedgerEntry {
  id: string;
  direction: 'credit' | 'debit';
  amount_cents: number;
  amount_microcents: number;
  balance_after_cents: number;
  balance_after_microcents: number;
  product: string;
  source: string;
  source_ref: string | null;
  note: string | null;
  created_at: string;
}

interface WalletBucket {
  id: string;
  display_name: string;
  price_eur_cents: number;
}

export function BillingPage() {
  const { t } = useTranslation('billing');
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'invoices';
  const setTab = useCallback(
    (next: Tab) => {
      const p = new URLSearchParams(params);
      p.set('tab', next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  const subscriptionFilter = params.get('subscription');

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
      </div>

      <nav
        className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800"
        aria-label={t('sectionsAria')}
      >
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            data-testid={`tab-${id}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {t(`tab.${id}`)}
          </button>
        ))}
      </nav>

      {tab === 'invoices' && <InvoicesTab subscriptionFilter={subscriptionFilter} />}
      {tab === 'orders' && <OrdersTab />}
      {tab === 'wallet' && <WalletTab />}
      {tab === 'payment-methods' && <PaymentMethodsTab />}
      {tab === 'profile' && <ProfileTab />}
    </div>
  );
}

// ---- Invoices tab ---------------------------------------------------

/**
 * OCP-532 - error keys the PDF endpoint answers with, in customer language.
 * `invoice_pdf_generating` is not a failure: Polar renders the document
 * asynchronously, so the honest answer is "it is being produced, try again"
 * rather than a dead download link or nothing at all.
 */
const INVOICE_PDF_ERRORS = new Set([
  'invoice_pdf_generating',
  'invoice_pdf_not_paid',
  'invoice_pdf_missing_billing_details',
  'invoice_pdf_not_available',
]);

function InvoicesTab({ subscriptionFilter }: { subscriptionFilter: string | null }) {
  const { t } = useTranslation('billing');
  const [items, setItems] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfPending, setPdfPending] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Invoice['status'] | 'all'>('all');

  const onDownload = useCallback(async (invoice: Invoice) => {
    // A mirrored, stable URL (Paddle) can be opened directly. Everything else
    // is resolved server-side per click.
    if (invoice.pdf_url) {
      window.open(invoice.pdf_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setPdfPending(invoice.id);
    setError(null);
    try {
      const r = await api<{ url: string }>(
        `/v1/portal/billing/invoices/${encodeURIComponent(invoice.id)}/pdf`,
      );
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      if (err instanceof ApiError && err.code && INVOICE_PDF_ERRORS.has(err.code)) {
        setError(t(`invoices.pdfError.${err.code}`));
      } else if (err instanceof ApiError) {
        setError(t('invoices.openErrorCode', { code: err.code ?? err.status }));
      } else {
        setError(t('invoices.openError'));
      }
    } finally {
      setPdfPending(null);
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (subscriptionFilter) params.set('subscription_id', subscriptionFilter);
      const qs = params.toString();
      try {
        const r = await api<{ items: Invoice[] }>(
          `/v1/portal/billing/invoices${qs ? '?' + qs : ''}`,
          { signal: ac.signal },
        );
        setItems(r.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('invoices.loadErrorHttp', { status: err.status }));
        else setError(t('invoices.loadError'));
      }
    })();
    return () => ac.abort();
  }, [statusFilter, subscriptionFilter, t]);

  return (
    <section>
      <div className="mb-3 flex items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          {t('invoices.statusLabel')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Invoice['status'] | 'all')}
            className="ml-2 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
          >
            <option value="all">{t('invoices.filter.all')}</option>
            <option value="paid">{t('invoices.filter.paid')}</option>
            <option value="open">{t('invoices.filter.open')}</option>
            <option value="void">{t('invoices.filter.void')}</option>
            <option value="refunded">{t('invoices.filter.refunded')}</option>
          </select>
        </label>
        {subscriptionFilter && (
          <span className="text-xs text-gray-500">
            {t('invoices.filteredTo')}<code className="font-mono">{subscriptionFilter}</code>
          </span>
        )}
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('invoices.loading')}</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('invoices.empty')}
        </div>
      )}

      {items && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">{t('invoices.col.date')}</th>
                <th className="px-3 py-2 text-left">{t('invoices.col.invoice')}</th>
                <th className="px-3 py-2 text-right">{t('invoices.col.amount')}</th>
                <th className="px-3 py-2 text-left">{t('invoices.col.status')}</th>
                <th className="px-3 py-2 text-right">{t('invoices.col.pdf')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {items.map((i) => (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatDate(i.issued_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">
                    {i.provider_invoice_number ?? i.provider_invoice_id}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(i.total_eur_cents, i.currency)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={i.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {i.pdf_available ? (
                      <button
                        type="button"
                        data-testid={`invoice-pdf-${i.id}`}
                        disabled={pdfPending === i.id}
                        onClick={() => void onDownload(i)}
                        className="text-brand-700 underline disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        {pdfPending === i.id ? t('invoices.opening') : t('invoices.download')}
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---- Orders tab (OCP-526) -------------------------------------------
//
// The customer's own contract record. The portal states the services, price,
// billing cycle and term before the order and the order confirmation repeats
// them (AGB V4 1.2: services and costs follow from the offer) - but until this tab existed there was
// nowhere a customer could look any of that up afterwards. The only record
// lived in the Merchant of Record's dashboard, which documents the PAYMENT,
// not the contract.
//
// Every value here comes from the SNAPSHOT taken when the order was placed,
// not from the current catalog - a repriced product must not rewrite what
// somebody ordered two years ago.

interface OrderRow {
  id: string;
  placed_at: string;
  placed_by_email: string;
  product_display_name: string;
  billing_cycle_label: string;
  term_note: string;
  tax_note: string;
  order_button_label: string;
  seat_count: number;
  unit_amount_cents: number;
  total_amount_cents: number;
  /** OCP-991 - what a discount code took off `total_amount_cents`; 0 without one. */
  discount_amount_cents?: number;
  currency: string;
  status: 'placed' | 'confirmed' | 'failed';
  confirmed_at: string | null;
  subscription_id: string | null;
  summary: { seat_based?: boolean; service_bullets?: string[]; discount?: { code: string; percent: number } | null } | null;
}

/** Badge classes per order status; labels are `orders.status.<key>` (OCP-656). */
const ORDER_STATUS_CLS: Record<OrderRow['status'], string> = {
  placed: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300',
  confirmed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  failed: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

function OrdersTab() {
  const { t } = useTranslation('billing');
  const [items, setItems] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<{ items: OrderRow[] }>('/v1/portal/orders', {
          signal: ac.signal,
        });
        setItems(r.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('orders.loadErrorHttp', { status: err.status }));
        else setError(t('orders.loadError'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  return (
    <section>
      <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
        {t('orders.intro')}
      </p>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('orders.loading')}</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('orders.empty')}
        </div>
      )}

      <ul className="space-y-3">
        {items?.map((o) => {
          const statusCls = ORDER_STATUS_CLS[o.status];
          const seatBased = o.summary?.seat_based === true;
          return (
            <li
              key={o.id}
              data-testid={`order-${o.id}`}
              className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {o.product_display_name}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {t('orders.orderedBy', { date: formatDate(o.placed_at), email: o.placed_by_email })}
                  </div>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusCls}`}>
                  {t(`orders.status.${o.status}`)}
                </span>
              </div>

              <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <OrderField label={t('orders.price')}>
                  {formatMoney(o.total_amount_cents, o.currency)}
                  {/* OCP-991 - the seat line stops adding up to a discounted total. */}
                  {seatBased && o.seat_count > 1 && !o.discount_amount_cents && (
                    <span className="ml-1 text-xs text-gray-500">
                      {t('orders.perSeat', { count: o.seat_count, unit: formatMoney(o.unit_amount_cents, o.currency) })}
                    </span>
                  )}
                  {!!o.discount_amount_cents && (
                    <span className="ml-1 text-xs text-gray-500" data-testid="order-discount">
                      {t('orders.discounted', {
                        amount: formatMoney(o.discount_amount_cents, o.currency),
                        code: o.summary?.discount?.code ?? '',
                      })}
                    </span>
                  )}
                </OrderField>
                <OrderField label={t('orders.billingCycle')}>{o.billing_cycle_label}</OrderField>
                {seatBased && <OrderField label={t('orders.seats')}>{o.seat_count}</OrderField>}
                {o.confirmed_at && (
                  <OrderField label={t('orders.confirmed')}>{formatDate(o.confirmed_at)}</OrderField>
                )}
              </dl>

              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{o.term_note}</p>
              <p className="mt-1 text-xs text-gray-500">{o.tax_note}</p>
              <p className="mt-2 font-mono text-[11px] text-gray-400">
                {t('orders.orderId', { id: o.id })}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function OrderField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-28 shrink-0 text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-gray-800 dark:text-gray-200">{children}</dd>
    </div>
  );
}

// ---- Wallet tab (OCP-318) -------------------------------------------

// €5 low-balance threshold in microcents (1 cent = 10,000) — OCP-372.
const LOW_BALANCE_MICROCENTS = 5_000_000;

function WalletTab() {
  const { t } = useTranslation('billing');
  const [balance, setBalance] = useState<number | null>(null);
  const [tokenRate, setTokenRate] = useState<number | null>(null);
  const [lots, setLots] = useState<Array<{ id: string; tokens: number; expires_at: string }>>([]);
  // OCP-786 - a refunded purchase whose tokens were already spent.
  const [refundDebt, setRefundDebt] = useState<number>(0);
  // OCP-668 (launch): orboto Token is the primary unit whenever a rate is published.
  const tokensPrimary = tokenRate !== null && tokenRate > 0;
  const [buckets, setBuckets] = useState<WalletBucket[]>([]);
  const [entries, setEntries] = useState<WalletLedgerEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);

  const loadLedger = useCallback(async (cursor: string | null) => {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const r = await api<{ entries: WalletLedgerEntry[]; next_cursor: string | null }>(
      `/v1/portal/wallet/ledger${qs}`,
    );
    setEntries((prev) => (cursor ? [...prev, ...r.entries] : r.entries));
    setNextCursor(r.next_cursor);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const [bal, prods] = await Promise.all([
          api<{ balance_cents: number; balance_microcents: number; orboto_token?: { microcents_per_token: number } | null; lots?: Array<{ id: string; tokens: number; expires_at: string }>; refund_debt_microcents?: number }>('/v1/portal/wallet/balance', {
            signal: ac.signal,
          }),
          api<{ items: Array<WalletBucket & { features?: { kind?: string } }> }>(
            '/v1/portal/products',
            { signal: ac.signal },
          ),
        ]);
        setBalance(bal.balance_microcents);
        setTokenRate(bal.orboto_token?.microcents_per_token ?? null);
        setLots(bal.lots ?? []);
        setRefundDebt(bal.refund_debt_microcents ?? 0);
        setBuckets(
          prods.items
            .filter(
              (p) => p.features?.kind === 'wallet_topup' || p.id.startsWith('wallet-topup-'),
            )
            .sort((a, b) => a.price_eur_cents - b.price_eur_cents)
            .map((p) => ({ id: p.id, display_name: p.display_name, price_eur_cents: p.price_eur_cents })),
        );
        await loadLedger(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('wallet.loadErrorHttp', { status: err.status }));
        else setError(t('wallet.loadError'));
      }
    })();
    return () => ac.abort();
  }, [loadLedger, t]);

  // OCP-526 - a wallet top-up is an order under the same Terms, so it gets the
  // same review step as every other buy entry point: services, price, billing
  // cycle and term on our surface, ordering button directly under the summary.
  // The step also hosts the OCP-524 responsible-contact capture when the
  // account still owes one.
  const orderReview = useOrderReview();

  const runTopUp = useCallback(async (productId: string) => {
    setBuying(productId);
    setError(null);
    try {
      const r = await api<{ checkout_url: string }>('/v1/portal/checkout/create', {
        method: 'POST',
        body: { product_id: productId },
      });
      window.location.assign(r.checkout_url);
    } catch (err) {
      setBuying(null);
      const gate = contractGateMessage(err);
      if (gate) setError(gate);
      else if (err instanceof ApiError)
        setError(t('wallet.checkoutErrorCode', { code: err.code ?? err.status }));
      else setError(t('wallet.checkoutError'));
    }
  }, [t]);

  const onTopUp = useCallback(
    (productId: string) => {
      orderReview.start({ productId, onOrder: () => runTopUp(productId) });
    },
    [orderReview, runTopUp],
  );

  const onLoadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      await loadLedger(nextCursor);
    } catch {
      setError(t('wallet.loadMoreError'));
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadLedger, t]);

  const low = balance !== null && balance < LOW_BALANCE_MICROCENTS;

  return (
    <section className="space-y-6">
      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Balance card */}
      <div
        data-testid="wallet-balance-card"
        className={`rounded-lg border p-5 shadow-sm ${
          low
            ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
        }`}
      >
        <p className="text-xs uppercase tracking-wide text-gray-500">{t('wallet.balance')}</p>
        <p className={`mt-1 text-3xl font-semibold ${low ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
          {balance === null ? '—' : tokensPrimary ? fmtOrbotoTokens(balance, tokenRate!) : formatMicrocents(balance, 'EUR')}
          {balance !== null && tokensPrimary ? (
            <span className="ml-2 text-sm font-normal text-gray-500" data-testid="wallet-balance-tokens">{t('wallet.eur', { eur: formatMicrocents(balance, 'EUR') })}</span>
          ) : null}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {t('wallet.balanceHelp')}
          {low && t('wallet.balanceLow')}
        </p>
        {refundDebt > 0 && (
          <p className="mt-2 text-xs text-red-700 dark:text-red-300" data-testid="wallet-refund-debt">
            {t('wallet.refundDebt', {
              amount: tokensPrimary ? fmtOrbotoTokens(refundDebt, tokenRate!) : formatMicrocents(refundDebt, 'EUR'),
            })}
          </p>
        )}
        {tokensPrimary && lots.length > 0 && (
          <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-2" data-testid="wallet-lots">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('wallet.lotsTitle')}</p>
            <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
              {lots.map((l) => (
                <li key={l.id}>{t('wallet.lotLine', { tokens: fmtNumber(l.tokens), date: fmtDate(l.expires_at) })}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Top-up bucket grid */}
      <div>
        <h2 className="mb-2 text-sm font-semibold">{t('wallet.addCredit')}</h2>
        {buckets.length === 0 ? (
          <p className="text-sm text-gray-500">{t('wallet.noTopups')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {buckets.map((b) => (
              <button
                key={b.id}
                type="button"
                data-testid={`wallet-bucket-${b.id}`}
                disabled={buying !== null}
                onClick={() => void onTopUp(b.id)}
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-4 text-center text-sm font-medium hover:border-brand-500 hover:text-brand-700 disabled:opacity-50"
              >
                {buying === b.id
                  ? t('wallet.opening')
                  : tokensPrimary
                    ? `${t('wallet.bucketTokens', { tokens: fmtNumber(Math.round((b.price_eur_cents * 10_000) / tokenRate!)) })} ${t('wallet.bucketFor', { price: formatCurrency(b.price_eur_cents, 'EUR') })}`
                    : formatCurrency(b.price_eur_cents, 'EUR')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Ledger */}
      <div>
        <h2 className="mb-2 text-sm font-semibold">{t('wallet.history')}</h2>
        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
            {t('wallet.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t('wallet.col.date')}</th>
                  <th className="px-3 py-2 text-left">{t('wallet.col.product')}</th>
                  <th className="px-3 py-2 text-right">{t('wallet.col.amount')}</th>
                  <th className="px-3 py-2 text-right">{t('wallet.col.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`wallet-ledger-${e.id}`}>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatDate(e.created_at)}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{productLabel(e.product)}</td>
                    <td
                      className={`px-3 py-2 text-right font-medium ${
                        e.direction === 'credit'
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {e.direction === 'credit' ? '+' : '−'}
                      {tokensPrimary ? fmtOrbotoTokens(e.amount_microcents, tokenRate!) : formatMicrocents(e.amount_microcents, 'EUR')}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {tokensPrimary ? fmtOrbotoTokens(e.balance_after_microcents, tokenRate!) : formatMicrocents(e.balance_after_microcents, 'EUR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {nextCursor && (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => void onLoadMore()}
              disabled={loadingMore}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              {loadingMore ? t('wallet.loading') : t('wallet.loadMore')}
            </button>
          </div>
        )}
      </div>

      {orderReview.element}
    </section>
  );
}

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const { t } = useTranslation('billing');
  const cls: Record<Invoice['status'], string> = {
    paid: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
    open: 'bg-sky-100 text-sky-800',
    void: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    refunded: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls[status]}`}>{t(`invoices.status.${status}`)}</span>;
}

// ---- Payment-methods tab --------------------------------------------

/**
 * OCP-532 - error keys the update-link endpoint answers with. Each one names a
 * different situation the customer can act on; the previous single
 * `no_paddle_link` collapsed "your plan is free" and "we cannot reach your
 * provider" into one meaningless message - and fired for every Polar-billed
 * customer, i.e. for everyone currently paying.
 */
const UPDATE_LINK_ERRORS = new Set([
  'no_billing_provider_link',
  'billing_provider_ambiguous',
  'no_polar_customer',
  'no_paddle_customer',
  'paddle_not_configured',
  'polar_not_configured',
]);

/**
 * OCP-532 - why a provider contributed no payment methods, in customer
 * language. `ok` needs no line: zero methods with a healthy read genuinely
 * means "no card saved".
 */
function sourceNotice(source: PaymentMethodSource): string | null {
  switch (source.outcome) {
    case 'ok':
      return null;
    case 'not_configured':
    case 'error':
      return i18n.t('billing:paymentMethods.sourceUnreachable');
    case 'no_customer_link':
      return i18n.t('billing:paymentMethods.sourceNoCustomer');
  }
}

function PaymentMethodsTab() {
  const { t } = useTranslation('billing');
  const { canManageBilling } = usePortalAccess();
  const [items, setItems] = useState<PaymentMethod[] | null>(null);
  const [sources, setSources] = useState<PaymentMethodSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [updatePending, setUpdatePending] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<{ items: PaymentMethod[]; sources: PaymentMethodSource[] }>(
          '/v1/portal/billing/payment-methods',
          { signal: ac.signal },
        );
        setItems(r.items);
        setSources(r.sources ?? []);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('paymentMethods.loadErrorHttp', { status: err.status }));
        else setError(t('paymentMethods.loadError'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  /**
   * OCP-532 - no `subscription_id` and no `prompt()`. Both providers attach the
   * new payment method to the CUSTOMER, so the server resolves which
   * subscription to mint the link for; it only asks for an explicit one when the
   * account is billed by two providers at once.
   */
  const onUpdate = useCallback(async () => {
    setUpdatePending(true);
    setError(null);
    try {
      const r = await api<{ update_url: string }>(
        '/v1/portal/billing/payment-methods/update-link',
      );
      window.location.assign(r.update_url);
    } catch (err) {
      setUpdatePending(false);
      if (err instanceof ApiError && err.code && UPDATE_LINK_ERRORS.has(err.code)) {
        setError(t(`paymentMethods.updateError.${err.code}`));
      } else if (err instanceof ApiError) {
        setError(t('paymentMethods.updateStartErrorCode', { code: err.code ?? err.status }));
      } else {
        setError(t('paymentMethods.updateStartError'));
      }
    }
  }, [t]);

  const hasUnverified = useMemo(
    () => (items ?? []).some((m) => m.status !== 'verified'),
    [items],
  );
  const notices = useMemo(
    () =>
      [...new Set(sources.map(sourceNotice).filter((n): n is string => n !== null))],
    [sources, t],
  );

  return (
    <section>
      <RoleReadOnlyNotice scope="billing" />
      {hasUnverified && items && items.length > 0 && (
        <div
          role="alert"
          data-testid="payment-method-warning-banner"
          className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          <strong>{t('paymentMethods.actionRequired')}</strong>
          {t('paymentMethods.actionRequiredText')}
        </div>
      )}

      {notices.map((n) => (
        <div
          key={n}
          role="status"
          data-testid="payment-method-source-notice"
          className="mb-4 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {n}
        </div>
      ))}

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('paymentMethods.loading')}</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('paymentMethods.empty')}
        </div>
      )}

      <ul className="space-y-3">
        {items?.map((m) => (
          <li
            key={`${m.provider}-${m.id}`}
            data-testid={`payment-method-${m.id}`}
            className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
          >
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {m.card_brand ? `${m.card_brand} ` : ''}
                {m.type === 'card' && m.last4 ? `•••• ${m.last4}` : m.type}
                {m.is_default && (
                  <span className="ml-2 rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                    {t('paymentMethods.default')}
                  </span>
                )}
              </p>
              {m.expires_month && m.expires_year && (
                <p className="text-xs text-gray-500">
                  {t('paymentMethods.expires', { month: String(m.expires_month).padStart(2, '0'), year: m.expires_year })}
                </p>
              )}
              <p className={`mt-1 text-xs ${m.status === 'verified' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                {m.status}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {/* OCP-532 - one control for the whole account, always reachable. It used
          to live inside a list row, so a customer whose list was empty (which
          was every Polar-billed customer) had no way to change their card at
          all - the exact path from an expiring card into dunning. */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="payment-method-update"
          onClick={() => void onUpdate()}
          hidden={!canManageBilling}
          disabled={updatePending}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:cursor-not-allowed disabled:bg-gray-200"
        >
          {updatePending ? t('paymentMethods.opening') : t('paymentMethods.update')}
        </button>
        <span className="text-xs text-gray-500">
          {t('paymentMethods.updateHelp')}
        </span>
      </div>
    </section>
  );
}

// ---- Profile tab ----------------------------------------------------

/**
 * OCP-532 - turn the mirror report into one sentence for the customer.
 *
 * `mirrored` and `not_needed` are silent successes. Everything else is
 * something the customer would otherwise never learn: their VAT-ID sits in our
 * database while the invoice keeps carrying the old tax fields. Keeping this
 * data current is only possible if they can see whether it arrived.
 */
function taxMirrorNotice(
  report: TaxMirrorReport | null,
): { tone: 'ok' | 'warn'; text: string } | null {
  if (!report) return null;
  switch (report.status) {
    case 'not_needed':
      return null;
    case 'mirrored':
      return { tone: 'ok', text: i18n.t('billing:profile.mirrored') };
    case 'no_billing_provider':
      return { tone: 'ok', text: i18n.t('billing:profile.noProvider') };
    case 'partial':
    case 'failed':
      return { tone: 'warn', text: i18n.t('billing:profile.mirrorFailed') };
  }
}

function ProfileTab() {
  const { t } = useTranslation('billing');
  const { canManageBilling } = usePortalAccess();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [mirror, setMirror] = useState<TaxMirrorReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);
  const [draft, setDraft] = useState<{
    company_name: string;
    vat_id: string;
    street: string;
    street2: string;
    city: string;
    postal_code: string;
    region: string;
    country: string;
  }>({
    company_name: '',
    vat_id: '',
    street: '',
    street2: '',
    city: '',
    postal_code: '',
    region: '',
    country: 'DE',
  });

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const p = await api<Profile>('/v1/portal/billing/profile', { signal: ac.signal });
        setProfile(p);
        setDraft({
          company_name: p.company_name ?? '',
          vat_id: p.vat_id ?? '',
          street: p.invoice_address?.street ?? '',
          street2: p.invoice_address?.street2 ?? '',
          city: p.invoice_address?.city ?? '',
          postal_code: p.invoice_address?.postal_code ?? '',
          region: p.invoice_address?.region ?? '',
          country: p.invoice_address?.country ?? 'DE',
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login?from=' + encodeURIComponent('/billing?tab=profile'));
          return;
        }
        setError(t('profile.loadError'));
      }
    })();
    return () => ac.abort();
  }, [navigate, t]);

  const onSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSavePending(true);
      setError(null);
      setMirror(null);
      try {
        const updated = await api<Profile>('/v1/portal/billing/profile', {
          method: 'PATCH',
          body: {
            company_name: draft.company_name || null,
            vat_id: draft.vat_id || null,
            invoice_address: {
              street: draft.street,
              street2: draft.street2 || null,
              city: draft.city,
              postal_code: draft.postal_code,
              region: draft.region || null,
              country: draft.country,
            },
          },
        });
        setProfile(updated);
        setMirror(updated.tax_mirror);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'vat_id_invalid') {
          setError(t('profile.vatInvalid'));
        } else if (err instanceof ApiError) {
          setError(t('profile.saveErrorCode', { code: err.code ?? err.status }));
        } else {
          setError(t('profile.saveError'));
        }
      } finally {
        setSavePending(false);
      }
    },
    [draft, t],
  );

  if (!profile) {
    return <p className="text-sm text-gray-500">{t('profile.loading')}</p>;
  }

  const notice = taxMirrorNotice(mirror);

  return (
    <form onSubmit={onSave} className="space-y-5">
      <RoleReadOnlyNotice scope="billing" className="mb-0" />
      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {notice && (
        <div
          role="status"
          data-testid="tax-mirror-notice"
          className={
            notice.tone === 'ok'
              ? 'rounded border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-200'
              : 'rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-900 dark:text-amber-200'
          }
        >
          {notice.text}
        </div>
      )}

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('profile.taxLegal')}
        </legend>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('profile.companyName')}</span>
          <input
            type="text"
            value={draft.company_name}
            onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('profile.vatId')}</span>
          <input
            type="text"
            value={draft.vat_id}
            onChange={(e) => setDraft({ ...draft, vat_id: e.target.value })}
            placeholder="DE123456789"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-gray-500">
            {t('profile.vatHelp')}
          </span>
        </label>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('profile.invoiceAddress')}
        </legend>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('profile.street')}</span>
          <input
            required
            type="text"
            value={draft.street}
            onChange={(e) => setDraft({ ...draft, street: e.target.value })}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('profile.street2')}</span>
          <input
            type="text"
            value={draft.street2}
            onChange={(e) => setDraft({ ...draft, street2: e.target.value })}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('profile.postalCode')}</span>
            <input
              required
              type="text"
              value={draft.postal_code}
              onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="col-span-2 block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('profile.city')}</span>
            <input
              required
              type="text"
              value={draft.city}
              onChange={(e) => setDraft({ ...draft, city: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('profile.region')}</span>
            <input
              type="text"
              value={draft.region}
              onChange={(e) => setDraft({ ...draft, region: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('profile.country')}</span>
            <input
              required
              maxLength={2}
              type="text"
              value={draft.country}
              onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm uppercase"
            />
          </label>
        </div>
      </fieldset>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={savePending || !canManageBilling}
          className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {savePending ? t('profile.saving') : t('profile.save')}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}

function formatCurrency(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(intlTag(), {
      style: 'currency',
      currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

// Wallet amounts are microcents (1 cent = 10,000) since OCP-372. Show
// sub-cent precision (up to 4 decimals) when an amount isn't a whole
// cent so a per-send Mail debit renders as e.g. -€0.0005, not €0.00.
/**
 * Map a raw wallet ledger `product` id to a brand-correct, human line-item
 * label (DOC-11: never surface internal codes; "orboto Mail"/"orboto AI" in
 * customer UI). Unknown products fall back to the raw id so a new metered
 * product still renders something. Exported for the unit test.
 */
export function productLabel(product: string): string {
  const known = new Set(['ai', 'mail-outbound', 'mail-inbound', 'storage', 'topup', 'adjustment', 'referral-credit', 'expiry']);
  return known.has(product) ? i18n.t(`billing:wallet.product.${product}`) : product;
}

function formatMicrocents(microcents: number, currency: string): string {
  const euros = microcents / 1_000_000;
  const wholeEuro = microcents % 1_000_000 === 0;
  const subCent = microcents % 10_000 !== 0;
  try {
    return new Intl.NumberFormat(intlTag(), {
      style: 'currency',
      currency,
      minimumFractionDigits: wholeEuro ? 0 : 2,
      maximumFractionDigits: subCent ? 4 : 2,
    }).format(euros);
  } catch {
    return `${euros.toFixed(subCent ? 4 : 2)} ${currency}`;
  }
}
